import { type RouteRecordRaw, createRouter, createWebHistory } from 'vue-router';
import * as Sentry from '@sentry/vue';
import { ApiError } from '@/api/errors';
import { isAuthenticated, isAuthenticatedByRL, logout, LogoutReason } from '@/modules/auth/auth';
import { trackView } from '@/libs/analytics';
import useFeatureFlags from '@/composables/feature-flags';
import { carryOpenReviewWithinProfile } from '@/modules/connection-reviews/connectionReviewDrawerRoute';
import api from '@/api/health';
import { requireConnectionCapability } from './guards/connection';
import { syncModeWithRoute } from './guards/mode';
import { reloadOnNewVersion } from './guards/newVersion';
import routes from './routes';

const scrollPositions: Record<string, number> = {};

const router = createRouter({
  history: createWebHistory(),
  routes: routes as RouteRecordRaw[],
  scrollBehavior(to, from, savedPosition) {
    const scrollableEl = document.querySelector('[data-scroll-target]');

    if (savedPosition) {
      if (scrollableEl && to.name) {
        const saved = scrollPositions[to.name as string];
        if (saved !== undefined) {
          scrollableEl.scrollTop = saved;
        }
      }
      return savedPosition;
    }

    if (to.hash) return { el: to.hash };

    if (to.path === from.path) return false;

    if (scrollableEl) {
      scrollableEl.scrollTop = 0;
    }
    return { top: 0 };
  },
});

// Save scroll position keyed by route name. We use a custom approach because the
// main scrollable element is not the document body — it's a container inside BasePageLayout.
router.beforeEach((_to, from, next) => {
  if (from.name) {
    const scrollableEl = document.querySelector('[data-scroll-target]');
    if (scrollableEl) {
      scrollPositions[from.name as string] = scrollableEl.scrollTop;
    }
  }
  next();
});

// After user logs in (or first page load), check that they can communicate to our backend.
// If the check fails due to network error then this likely means the user
// is having network issues with our API - the auth flow gives more info
// so we log them out (this is the quickest approach, not necessarily the best one).
let checkedNetwork = false;
router.beforeEach(async (_to, _from, next) => {
  const authenticated = await isAuthenticated();
  if (!authenticated) return next();

  // only do this check once, so we don't fetch API health on every route change
  if (checkedNetwork) return next();

  try {
    await api.fetchApiHealth();
    checkedNetwork = true;
  } catch (e) {
    logError(e);
    if (e instanceof TypeError || (e instanceof ApiError && !e.response)) {
      await logout(LogoutReason.NetworkError);
      window.location.href = '/';
      return next(false);
    }
  }
  next();
});

// On every route change (and first route), check to see we have feature flags set
// for the appropriate context (i.e. authenticated flags with user/org overrides vs public flags without overrides).
router.beforeEach(async (_to, _from, next) => {
  const authenticated = await isAuthenticatedByRL();
  try {
    await useFeatureFlags().setFlags(authenticated);
  } catch (e) {
    logError(e);
  }
  next();
});

// Global, so it also runs when only the supplier id changes, which a route's
// `beforeEnter` skips.
router.beforeEach(requireConnectionCapability);

router.beforeEach((to, from, next) => {
  const carried = carryOpenReviewWithinProfile(to, from);
  if (carried) return next(carried);
  next();
});

reloadOnNewVersion(router);

// @see: https://github.com/vitejs/vite/issues/11804#issuecomment-1406182566
// if the user has an older cached chunk, refresh the page to get the latest version
router.onError((error, to) => {
  if (error.message.includes('Failed to fetch dynamically imported module')) {
    Sentry.captureMessage('chunk error reload');
    // oxlint-disable-next-line no-console
    console.log('New version detected (chunk load error), refreshing the page...');
    window.location = to.fullPath as string & Location;
  }
});

syncModeWithRoute(router);

router.afterEach((to, from) => {
  trackView(to, from);
});

export default router;
